// Sentry error monitoring — frontend.
//
// Same privacy contract as the backend: no user identifiers, no health
// values, no auth tokens. We initialize here BEFORE React renders (see
// index.js) so a boot-time crash still gets captured.
//
// Only actual JS exceptions bubble up to Sentry; the axios interceptor
// already handles 4xx quietly and shows toasts, so we don't double-report.

import * as Sentry from "@sentry/react";

const NETWORK_NOISE = [
  /failed to fetch/i,
  /network request failed/i,
  /networkerror/i,
  /load failed/i,
  /the user aborted a request/i,
  /aborterror/i,
  /chunkloaderror/i,
  /loading chunk [\d]+ failed/i,
  /dynamically imported module/i,
  /resizeobserver loop/i,
];

const EXPECTED_STATUS = [401, 403, 404, 409, 422, 429];

function redact(v) {
  if (typeof v !== "string") return v;
  return v
    .replace(/Bearer\s+[\w.\-_+/=]+/gi, "Bearer [Filtered]")
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[email]")
    .replace(/([?&](?:token|access_token|email|phone|user_id)=)[^&]*/gi, "$1[Filtered]");
}

function scrub(event) {
  delete event.user;
  if (event.request) {
    delete event.request.cookies;
    delete event.request.headers;
    delete event.request.data;
    if (event.request.url) event.request.url = redact(event.request.url);
    delete event.request.query_string;
  }
  if (event.message) event.message = redact(event.message);
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = redact(ex.value);
    }
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs
      .filter((b) => !["http", "fetch"].includes(b.category))
      .map((b) => ({ ...b, data: undefined, message: b.message ? redact(b.message) : b.message }));
  }
  return event;
}

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "production",
    release: process.env.REACT_APP_SENTRY_RELEASE,
    sendDefaultPii: false,
    maxBreadcrumbs: 20,
    ignoreErrors: NETWORK_NOISE,
    tracesSampleRate: 0, // errors only — no perf tracing
    beforeSend(event, hint) {
      const err = hint?.originalException;
      const status = err?.response?.status ?? err?.status;
      if (status && EXPECTED_STATUS.includes(status)) return null;

      const text = `${event.message || ""} ${err?.message || ""}`;
      if (NETWORK_NOISE.some((r) => r.test(text))) return null;

      return scrub(event);
    },
  });
}

export { Sentry };
